import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { THEMES } from '../../data/product/product.data';

type Gen = { key: string; label: string; desc: string; dots: { angle: number; r: number; color: string }[] };

// The generator picker of the palette and gradient tools (owner 2026-10-12): a button showing the chosen
// generator, opening a one column list of every generator, each drawn as where its colours sit on the wheel
// with a line on what it makes; Codigrate Themes opens the themes to pick one. The host keeps the state.
@Component( {
  selector: 'app-generator-picker',
  templateUrl: './generator-picker.component.html',
  styleUrls: [ './generator-picker.component.scss' ],
} )
export class GeneratorPickerComponent {

  constructor( private host: ElementRef<HTMLElement> ) {
  }

  genOpen = false;
  // the theme list inside the generator pop-over
  themeView = false;
  // the generator chosen (a scheme key, 'theme', 'image', 'featured'); the host presets it
  selectedScheme = '';

  @Input() set scheme( v: string ) {
    this.selectedScheme = v || '';
  }

  // a theme picked for Codigrate Themes ('' = any theme); the host presets it to the theme it shows
  selectedTheme = '';

  @Input() set themeKey( v: string | undefined ) {
    this.selectedTheme = v || '';
  }

  // the gradient tool also offers its curated two colour gradients
  @Input() featured = false;

  @Output() schemeChange = new EventEmitter<string>();
  @Output() themeChange = new EventEmitter<string>();

  // every theme with a palette, for the theme list
  readonly themes: { key: string; name: string; logo: string; colors: string[] }[] = THEMES
    .map( line => line.products ).reduce( ( a, b ) => a.concat( b ), [] )
    .filter( p => p.palette && p.palette.length >= 3 )
    .map( p => ( { key: p.key, name: p.name, logo: p.logo, colors: ( p.compactPalette || [] ).slice( 0, 5 ) } ) )
    .sort( ( a, b ) => a.name.localeCompare( b.name ) );

  // each generator with its line and its dots on the wheel (angle from the top, clockwise)
  private readonly allGens: Gen[] = [
    { key: 'theme', label: 'Codigrate Themes', desc: 'The colors of one of our themes', dots: [] },
    { key: 'featured', label: 'Featured', desc: 'A curated gradient of two colors', dots: [] },
    { key: 'image', label: 'From Image', desc: 'The main colors of a picture you upload', dots: [] },
    { key: 'analogous', label: 'Analogous', desc: 'Neighbors on the wheel, calm and close', dots: this.dots( [ -30, 0, 30 ] ) },
    { key: 'monochrome', label: 'Monochrome', desc: 'One hue from dark to light', dots: [ 4, 8, 12 ].map( ( r, i ) => ( { angle: 200, r, color: `hsl(200 70% ${ 30 + i * 18 }%)` } ) ) },
    { key: 'complementary', label: 'Complementary', desc: 'Two opposite hues, strong contrast', dots: this.dots( [ 0, 180 ] ) },
    { key: 'split complementary', label: 'Split Complementary', desc: 'A hue and the two beside its opposite', dots: this.dots( [ 0, 150, 210 ] ) },
    { key: 'triadic', label: 'Triadic', desc: 'Three hues evenly around the wheel', dots: this.dots( [ 0, 120, 240 ] ) },
    { key: 'tetradic', label: 'Tetradic', desc: 'Four hues in two opposite pairs', dots: this.dots( [ 0, 90, 180, 270 ] ) },
    { key: 'warm', label: 'Warm', desc: 'Reds, oranges and yellows', dots: this.dots( [ -20, 10, 40 ] ) },
    { key: 'cool', label: 'Cool', desc: 'Greens, blues and purples', dots: this.dots( [ 140, 190, 240 ] ) },
    { key: 'full', label: 'Full Random', desc: 'Anything goes, a surprise every time', dots: this.dots( [ 20, 95, 165, 250, 310 ], [ 12, 6, 10, 8, 11 ] ) },
  ];

  get gens(): Gen[] {
    return this.featured ? this.allGens : this.allGens.filter( g => g.key !== 'featured' );
  }

  private dots( angles: number[], radii?: number[] ): { angle: number; r: number; color: string }[] {
    return angles.map( ( angle, i ) => ( { angle, r: radii ? radii[ i ] : 12, color: `hsl(${ ( angle + 360 ) % 360 } 75% 55%)` } ) );
  }

  get currentGen() {
    return this.gens.find( g => g.key === this.selectedScheme );
  }

  get currentTheme() {
    return this.themes.find( t => t.key === this.selectedTheme );
  }

  get currentGenDesc(): string {
    if ( !this.currentGen ) { return 'Pick how the colors are made'; }
    if ( this.currentGen.key === 'theme' ) { return this.currentTheme ? this.currentTheme.name : 'Random Theme'; }
    return this.currentGen.desc;
  }

  // the drop-down runs down to the foot of the window when there is room (owner 2026-10-12)
  genMaxHeight = 440;

  toggleGen( button?: HTMLElement ): void {
    this.genOpen = !this.genOpen;
    this.themeView = false;
    if ( this.genOpen && button && typeof window !== 'undefined' ) {
      this.genMaxHeight = Math.max( 260, Math.round( window.innerHeight - button.getBoundingClientRect().bottom - 8 - 16 ) );
    }
  }

  pickGen( key: string ): void {
    this.selectedScheme = key;
    this.schemeChange.emit( key );
    if ( key === 'theme' ) {
      this.themeView = true;
      return;
    }
    this.genOpen = false;
  }

  pickTheme( key: string ): void {
    this.selectedTheme = key;
    this.themeChange.emit( key );
    this.genOpen = false;
    this.themeView = false;
  }

  @HostListener( 'document:keydown.escape' )
  onEscape(): void {
    this.genOpen = false;
  }

  // close on a click anywhere outside the picker
  @HostListener( 'document:click', [ '$event' ] )
  onDocumentClick( event: MouseEvent ): void {
    if ( this.genOpen && !this.host.nativeElement.contains( event.target as Node ) ) {
      this.genOpen = false;
    }
  }
}
